import { useContext, useEffect, useRef, useState } from "react";
import { AppStateContext } from "../contexts/WorkoutContext";
import { useToast } from "../contexts/ToastContext";
import {
  applyUpdate,
  onUpdateReady,
} from "../serviceWorkerRegistration";
import {
  canAutoApplyUpdate,
  canPromptUpdate,
} from "../utils/serviceWorkerUpdatePolicy";

/**
 * Takes a waiting build by itself when nobody is using the app, and otherwise
 * offers it, once per build, at a moment that is not rude.
 *
 * "Nobody is using it" means: the app has just been opened or brought back to
 * the front, nothing has been tapped since, and no session or generation is
 * under way. An installed Android app is mostly resumed rather than relaunched,
 * so this is what keeps it current without a restart.
 *
 * Dismissing the prompt holds that build until the next resume; a newer build
 * arriving in the meantime is offered again.
 */
export function useServiceWorkerUpdate() {
  const { appState } = useContext(AppStateContext);
  const { showToast } = useToast();
  const [readyWorker, setReadyWorker] = useState(null);
  // Bumped on every resume so the effect below re-runs with fresh "interacted".
  const [resumeCount, setResumeCount] = useState(0);
  const interacted = useRef(false);
  const promptedFor = useRef(null);

  useEffect(() => onUpdateReady((worker) => setReadyWorker(worker)), []);

  useEffect(() => {
    const onInput = () => {
      interacted.current = true;
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      interacted.current = false;
      setResumeCount((n) => n + 1);
    };
    window.addEventListener("pointerdown", onInput, true);
    window.addEventListener("keydown", onInput, true);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pointerdown", onInput, true);
      window.removeEventListener("keydown", onInput, true);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    const updateReady = Boolean(readyWorker);
    if (
      canAutoApplyUpdate({
        updateReady,
        appState,
        interacted: interacted.current,
      })
    ) {
      applyUpdate();
      return;
    }
    if (
      !canPromptUpdate({
        updateReady,
        appState,
        prompted: promptedFor.current === readyWorker,
      })
    ) {
      return;
    }

    promptedFor.current = readyWorker;
    showToast({
      title: "New version available",
      description: "Reload to pick up the latest build.",
      duration: 0, // Persistent — the user decides, not a timeout.
      action: { label: "Reload", onClick: applyUpdate },
    });
  }, [readyWorker, appState, resumeCount, showToast]);
}

export default useServiceWorkerUpdate;
